import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import jwtDecode from 'jwt-decode'

import accountAnalytics from '~/analytics/account/accountAnalytics'

import { callApi } from '~/services/axiosInstance.service'
import { getFirebase } from '~/firebase'
import { AppDispatch, RootState } from '~/redux/store'
import authService from '~/services/auth.service'
import { resetCaptcha } from '~/auth/slices/recaptcha.slice'
// import { homApi } from '~/redux/apis/hom'

import { UserRole } from '~/types/user-info-types'

import userConfig from '~/config/app'

import { Result, AuthenticationResponse } from '~/redux/apis/types'

import { LoginType } from '~/analytics/account/accountEvents.types'
import nativeApp from '~/shared/native-app/native-app'
import { FrontendMessageType } from '~/shared/native-app/types/native-app.types'
import { setIsPreSignupActionCreator } from '~/onboarding/redux/onboarding.slice'
import { startListening } from '~/redux/listenerMiddleware'
import { clearProgressMigrationModalSessionForUser } from '~/curriculum/utils/progressMigrationModalSession.utils'
import { identifyUserWithPosthog, resetPosthogIdentity } from '~/utils/analytics/posthogUtils'

interface JwtPayload {
  id: number
  role: string
  iat: number
  exp: number
  oracle: boolean
}
interface AuthState {
  jwtPayload: null | JwtPayload
  firebaseAuthenticated: boolean
}

const initialState: AuthState = {
  jwtPayload: null,
  firebaseAuthenticated: false,
}

const modulePrefix = 'auth'

const slice = createSlice({
  name: modulePrefix,
  initialState,
  reducers: {
    setFirebaseAuthenticated(state, action: PayloadAction<boolean>) {
      state.firebaseAuthenticated = action.payload
    },
    tokenReceived(state, action: PayloadAction<{ firebaseToken: string | null; accessToken: string | null } | null>) {
      if (action.payload) {
        state.jwtPayload = action.payload.accessToken ? (jwtDecode(action.payload.accessToken) as JwtPayload) : null
      } else {
        state.jwtPayload = null
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
  },
})

// SELECTORS
export const selectIsFirebaseAuthenticated = (state: RootState): boolean => state.auth.firebaseAuthenticated
export const selectUserId = (state: RootState): number | null =>
  state.auth.jwtPayload ? state.auth.jwtPayload.id : null
export const selectUserIsAdmin = (state: RootState): boolean =>
  state.auth.jwtPayload ? state.auth.jwtPayload.role === UserRole.ADMIN : false
export const selectUserIsTutor = (state: RootState): boolean =>
  state.auth.jwtPayload ? state.auth.jwtPayload.role === UserRole.TUTOR : false
export const selectUserIsSchoolTeacher = (state: RootState): boolean =>
  state.auth.jwtPayload ? state.auth.jwtPayload.role === UserRole.SCHOOL_TEACHER : false
export const selectUserIsSchoolStudent = (state: RootState): boolean =>
  state.auth.jwtPayload ? state.auth.jwtPayload.role === UserRole.SCHOOL_STUDENT : false

export const selectJwtExpiresInSeconds = (state: RootState): number | null =>
  state.auth.jwtPayload ? state.auth.jwtPayload.exp : null

export const isAuthenticated = createSelector(
  [selectJwtExpiresInSeconds],
  (jwtExpiresAt): boolean => jwtExpiresAt != null && jwtExpiresAt > 0,
)

// ACTIONS
export const logout = createAction<void>('auth/logout')

export const { tokenReceived, setFirebaseAuthenticated } = slice.actions

export const updateToken =
  (tokens: { firebaseToken: string; accessToken: string } | null) => (dispatch: AppDispatch) => {
    authService.setSession(tokens)

    if (tokens) {
      getFirebase().then(({ signInWithCustomToken, auth }) => signInWithCustomToken(auth, tokens.firebaseToken))
    }
    dispatch(tokenReceived(tokens))
  }

export const updateFirebaseToken = (firebaseToken: string | null) => (dispatch: AppDispatch) => {
  authService.setFirebaseToken(firebaseToken)

  if (firebaseToken) {
    getFirebase().then(({ signInWithCustomToken, auth }) => signInWithCustomToken(auth, firebaseToken))
  }

  dispatch(setFirebaseAuthenticated(true))
}

export const logoutUser = () => async (dispatch: AppDispatch, getState: any) => {
  const userId = getState()?.user?.current?.id
  if (userId) {
    clearProgressMigrationModalSessionForUser(userId)
  }
  dispatch(updateToken(null))
  dispatch(logout())
  // Reset the posthog identity as part of logout. Doing it synchronously keeps it deterministically
  // ordered before any later identify(), so the next user on this device is never identified against
  // the previous user's session (which would merge the two into one posthog person).
  resetPosthogIdentity()

  if (userConfig.isMobileApp) {
    nativeApp.SendMessage({ type: FrontendMessageType.Logout })
  }

  // dispatch(setFirebaseAuthenticated(false))
  // dispatch(homApi.util.resetApiState())

  // await dispatch(authApi.endpoints.logOut())
  await Promise.all([
    callApi({
      url: '/v1/auth/logout',
      method: 'GET',
    }),
    getFirebase().then(({ signOut, auth }) => signOut(auth)),
  ])
  accountAnalytics.logLogOut(userId)
}

interface LoginUserArgs {
  loginType: LoginType
  result: Result<AuthenticationResponse>
}
export const loginUser =
  ({ loginType, result }: LoginUserArgs) =>
  (dispatch: AppDispatch) => {
    if (result.type && result.type === 'success') {
      dispatch(resetCaptcha())
      accountAnalytics.logSignIn(loginType, result.data.user.id, result.data.log)
      // A same-user re-login leaves /me unchanged, so useInitApp wouldn't re-identify
      identifyUserWithPosthog(result.data.user)

      dispatch(updateToken(result.data))
      dispatch(setIsPreSignupActionCreator(false))
    }
  }

// Make sure posthog is reset whenever the user logs out (or switches to another family profile)
startListening({
  actionCreator: logout,
  effect: () => {
    resetPosthogIdentity()
  },
})

export default slice.reducer
